<template>
  <section
    class="popular-features scrollable"
    :class="{ desktop: $isDesktop }"
    :style="{ gridTemplateColumns: `repeat(${columnCount}, 34vw)` }"
  >
    <nuxt-link
      v-for="(card, index) of popularFeatures"
      :to="$puLocalePath({ path: card.url })"
      :style="{
        order: card.order && !$isDesktop ? card.order : 20,
      }"
    >
      <div class="picture">
        <img
          :loading="isLazyImg(index) ? 'lazy' : 'eager'"
          :src="
            $isDesktop
              ? `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=65,w=224,h=161${card.image}`
              : `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=65,w=140,h=100${card.image}`
          "
          :alt="card.name"
          width="140"
          height="100"
        />
      </div>
      <span>{{ card.name }}</span>
    </nuxt-link>
  </section>
</template>

<script setup lang="ts">
import { useLazyAsyncData } from 'nuxt/app'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'

interface PopularFeature {
  url: string
  name: string
  image: string
  order: number
}

const langCode = (useRoute().params?.lang as string) || 'en-gb'

const { data: popularFeatures } = await useLazyAsyncData(
  `popularFeatures-${langCode}`,
  () =>
    $fetch<PopularFeature[]>(
      getLangFallbackUrl(langCode, '/_/popular-features/'),
    ),
)

const columnCount = computed(() => {
  return Math.round((popularFeatures.value?.length ?? 0) / 2)
})

const isLazyImg = (index: number): boolean => {
  return index > 5
}
</script>

<style scoped>
.popular-features {
  column-gap: 0.8em;
  display: grid;
  grid-auto-rows: minmax(34vw, auto);
  row-gap: 0.75em;
}

a {
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  text-decoration: none;
  position: relative;
}

a:active {
  filter: brightness(0.5);
  transform: scale(1.1);
}

a span {
  align-items: flex-start;
  color: var(--colour-dark-blue);
  display: flex;
  font-size: var(--body);
  justify-content: flex-start;
  line-height: 1.1;
  margin: 0;
  padding: 0.4em 0 0;
}

.picture {
  overflow: hidden;
  height: 24vw;
  border-radius: 0.65em;
  background-color: var(--colour-page-background);
  padding: 0;
}

.picture img {
  aspect-ratio: auto 4 / 3;
  height: auto;
  max-width: none;
  width: 100%;
}

/* desktop & tablet */
.popular-features.desktop {
  grid-template-columns: repeat(8, 14em) !important;
  column-gap: 3em;
  grid-auto-rows: 12em;
  row-gap: 3em;
  margin: 0;
}

.desktop a {
  transition: transform 0.25s ease-out;
  overflow: visible;
}

.desktop a:hover,
.desktop a:focus-visible {
  transform: scale(1.05);
  outline: none;
}

.desktop a span {
  font-size: 1.3em;
  padding: 0.55em 0 0;
}

.desktop .picture {
  height: auto;
  transition: box-shadow 0.15s ease-out;
}

.desktop a:hover .picture,
.desktop a:focus-visible .picture {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
}
</style>
